/**
 * SOUND MANAGER — event-driven SFX playback (Web Audio) + streamed BGM (HTMLAudioElement).
 * Browsers that ignore HTMLMediaElement.volume (mobile Safari) route BGM through a GainNode.
 */
import * as C from "../config/constants.js";
import { resolveSoundFileUrl } from "../assets/sound-urls.js";
import { EventBus } from "./event-bus.js";
import { StorageSlot, readStorageRecord, writeStorageRecord } from "./storage.js";
import { resolveGameStorage } from "./persistence-backend.js";
import { createSfxEngine } from "./sfx-engine.js";

export function SoundManager() {
  this.enabled = true;
  /** In-game / portal user mute (CrazyGames `muteAudio`, dev perf). */
  this.userMuted = false;
  /** YouTube host audio gate — combined in {@link SoundManager#syncOutputMuteState}. */
  this.platformMuted = false;
  /** Effective output mute (userMuted || platformMuted). */
  this.muted = false;
  this.unlocked = false;
  this.lastPlayedAt = {};
  this.cooldownsMs = {
    just_fall: 80,
    gem: 50,
  };
  this._lastBounceSfxAt = 0;
  this.manifest = {
    gem: "gem.mp3",
    just_fall: "just_fall.mp3",
    shield: "shield.mp3",
    shield_boing: "shield-boing.mp3",
    merge: "merge.mp3",
    ding1: "ding1.mp3",
    ding2: "ding2.mp3",
    ding3: "ding3.mp3",
    ding4: "ding4.mp3",
    ding5: "ding5.mp3",
    boulder_explode: "boulder-crush.mp3",
    flask_hit: "flask-hit.mp3",
    flask_smash: "flask-smash.mp3",
    pickup: "pickup.mp3",
    gold_orb: "gold-orb.mp3",
    life_up: "life-up.mp3",
    shake_drop: "shake-drop.mp3",
    shrink: "shrink.mp3",
    wheel_spin: "wheel-spin.mp3",
    game_reward: "game-reward.mp3",
  };
  /** Looping stage BGM (streamed via HTMLAudioElement; loudness may use a Web Audio gain). */
  this.bgmManifest = {
    fall: "bgm-fall.mp3",
    shake: "bgm-shake.mp3",
  };
  this.bgmVolume = 0.2;
  this.sfxVolume = 0.8;
  this.bgmTracks = {};
  this.bgmScene = null;
  this.bgmPaused = false;
  /** Tab/window unfocused — block new SFX and stop active one-shots. */
  this.focusPaused = false;
  this._pendingBgmScene = null;
  /** True when BGM play() was blocked and needs a user gesture. */
  this.bgmAutoplayBlocked = false;
  /** True when SFX AudioContext resume was blocked and needs a user gesture. */
  this.sfxAutoplayBlocked = false;
  this._bgmFadeRaf = null;
  /** @type {{ mode: "out"|"in", scene: string, startAt: number, durationMs: number, startVol?: number }|null} */
  this._bgmFade = null;
  /** @type {ReturnType<typeof createSfxEngine>|null} */
  this.sfxEngine = null;
}

SoundManager.prototype._clampVolume = function (volume) {
  var n = Number(volume);
  if (!isFinite(n)) return 0;
  return Math.max(0, Math.min(1, n));
};

/**
 * Mobile Safari keeps HTMLMediaElement.volume at 1 and ignores writes.
 * @returns {boolean}
 */
SoundManager.prototype._browserIgnoresMediaVolume = function () {
  if (this._mediaVolumeIgnored != null) return this._mediaVolumeIgnored;
  this._mediaVolumeIgnored = false;
  if (typeof Audio === "undefined") return false;
  try {
    var probe = new Audio();
    var written = 0.2;
    probe.volume = written;
    this._mediaVolumeIgnored = Math.abs(Number(probe.volume) - written) > 0.05;
  } catch (e) {
    this._mediaVolumeIgnored = true;
  }
  return this._mediaVolumeIgnored;
};

/**
 * @param {HTMLAudioElement|null} track
 * @returns {GainNode|null}
 */
SoundManager.prototype._ensureTrackGain = function (track) {
  if (!track || !this._browserIgnoresMediaVolume()) return null;
  if (track._bgmOutputGain) return track._bgmOutputGain;
  if (!this.sfxEngine || typeof this.sfxEngine.connectMediaElement !== "function") return null;
  return this.sfxEngine.connectMediaElement(track);
};

/**
 * @param {HTMLAudioElement|null} track
 * @param {number} volume
 */
SoundManager.prototype._setTrackVolume = function (track, volume) {
  if (!track) return;
  var vol = this._clampVolume(volume);
  var gain = this._ensureTrackGain(track);
  if (gain && gain.gain) {
    try {
      track.volume = 1;
      gain.gain.value = vol;
      return;
    } catch (e) { /* fall through to the element */ }
  }
  try {
    track.volume = vol;
  } catch (e2) { /* ignore */ }
};

/**
 * @param {HTMLAudioElement|null} track
 * @returns {number}
 */
SoundManager.prototype._readTrackVolume = function (track) {
  if (!track) return 0;
  if (track._bgmOutputGain && track._bgmOutputGain.gain) {
    var g = Number(track._bgmOutputGain.gain.value);
    if (isFinite(g)) return this._clampVolume(g);
  }
  var v = Number(track.volume);
  return isFinite(v) ? this._clampVolume(v) : 0;
};

SoundManager.prototype._defaultBgmVolume = function () {
  return (C.DEFAULT_BGM_VOLUME != null) ? Number(C.DEFAULT_BGM_VOLUME) : 0.2;
};

SoundManager.prototype._defaultSfxVolume = function () {
  return (C.DEFAULT_SFX_VOLUME != null) ? Number(C.DEFAULT_SFX_VOLUME) : 0.8;
};

SoundManager.prototype._getSfxGain = function (name) {
  var table = (typeof C.SFX_GAIN_BY_SOUND === "object" && C.SFX_GAIN_BY_SOUND) ? C.SFX_GAIN_BY_SOUND : null;
  if (!table || name == null) return 1;
  var gain = table[name];
  if (gain == null || !isFinite(Number(gain)) || Number(gain) <= 0) return 1;
  return Number(gain);
};

SoundManager.prototype._createSfxEngine = function () {
  var self = this;
  return createSfxEngine(this.manifest, {
    resolveUrl: resolveSoundFileUrl,
    getSoundGain: function (name) { return self._getSfxGain(name); },
    clampVolume: function (volume) { return self._clampVolume(volume); },
  });
};

SoundManager.prototype.loadSettings = function () {
  var storage = resolveGameStorage(null);
  if (!storage) {
    this.bgmVolume = this._clampVolume(this._defaultBgmVolume());
    this.sfxVolume = this._clampVolume(this._defaultSfxVolume());
    return;
  }
  try {
    var parsed = readStorageRecord(storage, StorageSlot.SOUND_SETTINGS);
    if (!parsed) {
      this.bgmVolume = this._clampVolume(this._defaultBgmVolume());
      this.sfxVolume = this._clampVolume(this._defaultSfxVolume());
      return;
    }
    this.bgmVolume = this._clampVolume(
      parsed && parsed.bgmVolume != null ? parsed.bgmVolume : this._defaultBgmVolume()
    );
    this.sfxVolume = this._clampVolume(
      parsed && parsed.sfxVolume != null ? parsed.sfxVolume : this._defaultSfxVolume()
    );
  } catch (e) {
    this.bgmVolume = this._clampVolume(this._defaultBgmVolume());
    this.sfxVolume = this._clampVolume(this._defaultSfxVolume());
  }
};

SoundManager.prototype.saveSettings = function () {
  var storage = resolveGameStorage(null);
  if (!storage) return;
  try {
    writeStorageRecord(storage, StorageSlot.SOUND_SETTINGS, {
      bgmVolume: this.bgmVolume,
      sfxVolume: this.sfxVolume,
    });
  } catch (e) { /* ignore */ }
};

SoundManager.prototype.getBgmVolume = function () {
  return this.bgmVolume;
};

SoundManager.prototype.getSfxVolume = function () {
  return this.sfxVolume;
};

SoundManager.prototype.setBgmVolume = function (volume) {
  this.bgmVolume = this._clampVolume(volume);
  this._syncBgmTrackVolumes();
};

SoundManager.prototype.setSfxVolume = function (volume) {
  this.sfxVolume = this._clampVolume(volume);
  if (this.sfxEngine) this.sfxEngine.setMasterGain(this.sfxVolume);
};

SoundManager.prototype._syncBgmTrackVolumes = function () {
  var keys = Object.keys(this.bgmTracks);
  for (var i = 0; i < keys.length; i++) {
    var track = this.bgmTracks[keys[i]];
    if (!track) continue;
    var scene = keys[i];
    if (this._isFadeOutActive(scene) || this._isFadeInActive(scene)) continue;
    this._setTrackVolume(track, this.bgmVolume);
  }
};

SoundManager.prototype._createAudio = function (srcFile) {
  var url = resolveSoundFileUrl(srcFile);
  if (!url) return null;
  var audio = new Audio(url);
  audio.preload = "auto";
  return audio;
};

/**
 * User / portal-settings mute (CrazyGames `game.settings.muteAudio`).
 * @param {boolean} value
 */
SoundManager.prototype.setMuted = function (value) {
  this.userMuted = !!value;
  this.syncOutputMuteState();
};

/**
 * Host audio gate (YouTube `ytgame.system.isAudioEnabled`).
 * @param {boolean} value — true when host requires muted output
 */
SoundManager.prototype.setPlatformMuted = function (value) {
  var next = !!value;
  if (next === this.platformMuted) return;
  this.platformMuted = next;
  this.syncOutputMuteState();
  EventBus.emit("sound:platform_mute", { platformMuted: this.platformMuted });
};

SoundManager.prototype.isPlatformMuted = function () {
  return !!this.platformMuted;
};

/** Applies combined user + platform mute to SFX and BGM tracks. */
SoundManager.prototype.syncOutputMuteState = function () {
  var effective = !!(this.userMuted || this.platformMuted);
  this.muted = effective;
  if (this.sfxEngine) this.sfxEngine.setMuted(this.muted);
  var bgmKeys = Object.keys(this.bgmTracks);
  for (var b = 0; b < bgmKeys.length; b++) {
    var track = this.bgmTracks[bgmKeys[b]];
    if (!track) continue;
    try {
      track.muted = this.muted;
      if (this.muted) track.pause();
      else if (this.bgmScene === bgmKeys[b] && !this.bgmPaused) this._startBgmPlayback(track);
    } catch (e2) { /* ignore */ }
  }
};

/**
 * Pause one-shot SFX when the game tab/window loses focus (gameplay may continue).
 * @param {boolean} paused
 */
SoundManager.prototype.setFocusPaused = function (paused) {
  this.focusPaused = !!paused;
  if (this.sfxEngine) this.sfxEngine.setFocusPaused(this.focusPaused);
};

SoundManager.prototype.prepareBgm = function () {
  var keys = Object.keys(this.bgmManifest);
  for (var i = 0; i < keys.length; i++) {
    var key = keys[i];
    var file = this.bgmManifest[key];
    if (!file) continue;
    var audio = this._createAudio(file);
    if (!audio) continue;
    audio.loop = true;
    audio.volume = this.bgmVolume;
    audio.muted = this.muted;
    this.bgmTracks[key] = audio;
  }
};

SoundManager.prototype._stopAllBgm = function () {
  var keys = Object.keys(this.bgmTracks);
  for (var i = 0; i < keys.length; i++) {
    this.stopBgmTrack(keys[i]);
  }
};

SoundManager.prototype._stopAllBgmExcept = function (exceptScene) {
  var keys = Object.keys(this.bgmTracks);
  for (var i = 0; i < keys.length; i++) {
    if (exceptScene && keys[i] === exceptScene) continue;
    this.stopBgmTrack(keys[i]);
  }
};

SoundManager.prototype._isFadeOutActive = function (scene) {
  return !!(scene && this._bgmFade && this._bgmFade.mode === "out" && this._bgmFade.scene === scene);
};

SoundManager.prototype._isFadeInActive = function (scene) {
  return !!(scene && this._bgmFade && this._bgmFade.mode === "in" && this._bgmFade.scene === scene);
};

/**
 * @param {HTMLAudioElement} audio
 * @returns {Promise<boolean>} true when playback started (or BGM intentionally silent)
 */
SoundManager.prototype._startBgmPlayback = function (audio) {
  var self = this;
  if (!audio || this.muted || !this.enabled) return Promise.resolve(false);
  if (this._browserIgnoresMediaVolume()) {
    this._ensureTrackGain(audio);
    if (this.sfxEngine && typeof this.sfxEngine.resumeContext === "function") {
      this.sfxEngine.resumeContext();
    }
  }
  if (this.bgmVolume <= 0) {
    this.bgmAutoplayBlocked = false;
    return Promise.resolve(true);
  }
  try {
    var p = audio.play();
    if (p && typeof p.then === "function") {
      return p.then(function () {
        self.bgmAutoplayBlocked = false;
        return true;
      }).catch(function () {
        self.bgmAutoplayBlocked = true;
        return false;
      });
    }
    this.bgmAutoplayBlocked = false;
    return Promise.resolve(true);
  } catch (e) {
    this.bgmAutoplayBlocked = true;
    return Promise.resolve(false);
  }
};

SoundManager.prototype._isBgmWanted = function () {
  return !!(
    this.enabled &&
    !this.muted &&
    !this.bgmPaused &&
    this.bgmVolume > 0 &&
    this._pendingBgmScene
  );
};

SoundManager.prototype._isSfxWanted = function () {
  return !!(this.enabled && !this.muted && this.sfxVolume > 0);
};

SoundManager.prototype.isBgmPlaying = function () {
  if (!this._pendingBgmScene) return false;
  var track = this.bgmTracks[this._pendingBgmScene];
  if (!track) return false;
  return !track.paused;
};

SoundManager.prototype.isBgmAutoplayBlocked = function () {
  if (!this._isBgmWanted()) return false;
  return this.bgmAutoplayBlocked || !this.isBgmPlaying();
};

/**
 * Try to start pending BGM (boot / resume). Confirms autoplay via play() result.
 * @returns {Promise<boolean>}
 */
SoundManager.prototype.ensureBgmPlayback = function () {
  var self = this;
  if (!this._isBgmWanted()) {
    this.bgmAutoplayBlocked = false;
    return Promise.resolve(true);
  }
  var scene = this._pendingBgmScene;
  var track = this.bgmTracks[scene];
  if (!track) return Promise.resolve(false);
  if (!track.paused) {
    this.bgmAutoplayBlocked = false;
    return Promise.resolve(true);
  }
  return this._startBgmPlayback(track).then(function (ok) {
    if (ok) {
      self.bgmScene = scene;
      self.bgmAutoplayBlocked = false;
    } else {
      self.bgmAutoplayBlocked = true;
    }
    return ok;
  });
};

SoundManager.prototype.needsGestureUnlock = function () {
  if (this.unlocked) return false;
  return this._isSfxWanted() || this._isBgmWanted();
};

SoundManager.prototype.isAudioAutoplayBlocked = function () {
  if (this.unlocked) return false;
  if (!this._isBgmWanted()) return false;
  return this.bgmAutoplayBlocked || !this.isBgmPlaying();
};

/** True when Web Audio SFX still need a user gesture (BGM may already be playing). */
SoundManager.prototype.isSfxAutoplayBlocked = function () {
  if (this.unlocked) return false;
  if (!this._isSfxWanted()) return false;
  return !!this.sfxAutoplayBlocked;
};

/** Probe SFX + BGM autoplay without marking unlocked until both succeed. */
SoundManager.prototype.ensureAudioPlayback = function () {
  return this._tryProgrammaticUnlock();
};

SoundManager.prototype._restoreBgmTrackVolumes = function () {
  var keys = Object.keys(this.bgmTracks);
  for (var i = 0; i < keys.length; i++) {
    var track = this.bgmTracks[keys[i]];
    if (track) this._setTrackVolume(track, this.bgmVolume);
  }
};

SoundManager.prototype._easeInOut = function (t) {
  return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
};

SoundManager.prototype._cancelBgmFade = function () {
  if (this._bgmFadeRaf != null && typeof cancelAnimationFrame === "function") {
    cancelAnimationFrame(this._bgmFadeRaf);
  }
  this._bgmFadeRaf = null;
  if (this._bgmFade) {
    this._restoreBgmTrackVolumes();
    this._bgmFade = null;
  }
};

SoundManager.prototype.stopBgmTrack = function (scene) {
  var track = scene ? this.bgmTracks[scene] : null;
  if (!track) return;
  try {
    track.pause();
    track.currentTime = 0;
    this._setTrackVolume(track, this.bgmVolume);
  } catch (e) { /* ignore */ }
  if (this.bgmScene === scene) this.bgmScene = null;
};

SoundManager.prototype._finishBgmFadeOut = function (scene) {
  var track = this.bgmTracks[scene];
  if (track) {
    try {
      track.pause();
      track.currentTime = 0;
      this._setTrackVolume(track, this.bgmVolume);
    } catch (e) { /* ignore */ }
  }
  if (this.bgmScene === scene) this.bgmScene = null;
  this._bgmFade = null;
  this._bgmFadeRaf = null;
};

SoundManager.prototype._finishBgmFadeIn = function (scene) {
  var track = this.bgmTracks[scene];
  var targetVol = this.bgmVolume;
  var keys = Object.keys(this.bgmTracks);
  for (var i = 0; i < keys.length; i++) {
    if (keys[i] === scene) continue;
    this.stopBgmTrack(keys[i]);
  }
  if (track) {
    try {
      this._setTrackVolume(track, targetVol);
      if (!this.bgmPaused && !this.muted) this._startBgmPlayback(track);
      else track.pause();
    } catch (e2) { /* ignore */ }
  }
  this.bgmScene = scene;
  this._pendingBgmScene = scene;
  this._bgmFade = null;
  this._bgmFadeRaf = null;
};

SoundManager.prototype._scheduleBgmFadeStep = function (stepFn) {
  var self = this;
  if (typeof requestAnimationFrame === "function") {
    this._bgmFadeRaf = requestAnimationFrame(stepFn);
  } else {
    stepFn(typeof performance !== "undefined" ? performance.now() : Date.now());
  }
};

/**
 * Fade out one BGM loop (e.g. fall in the last seconds of Phase I).
 * @param {"fall"|"shake"} scene
 * @param {number} [durationMs]
 */
SoundManager.prototype.fadeOutBgmScene = function (scene, durationMs) {
  if (!scene || !this.bgmTracks[scene]) return;
  if (this._bgmFade && this._bgmFade.mode === "out" && this._bgmFade.scene === scene) return;
  this._cancelBgmFade();
  this._pendingBgmScene = this.bgmScene;
  if (!this.enabled || !this.unlocked) return;

  var track = this.bgmTracks[scene];
  var ms = Math.max(100, Math.floor(durationMs || 2000));
  var startVol = this._readTrackVolume(track);
  if (!(startVol > 0)) startVol = this.bgmVolume;
  var startAt = typeof performance !== "undefined" ? performance.now() : Date.now();
  var self = this;

  if (!track.paused && !this.bgmPaused && !this.muted) this._startBgmPlayback(track);
  else if (track.paused && this.bgmScene === scene && !this.bgmPaused && !this.muted) {
    this._startBgmPlayback(track);
    startVol = this.bgmVolume;
  }

  this._bgmFade = { mode: "out", scene: scene, startAt: startAt, durationMs: ms, startVol: startVol };

  function step(now) {
    var fade = self._bgmFade;
    if (!fade || fade.mode !== "out" || fade.scene !== scene) return;
    var t = Math.min(1, (now - fade.startAt) / fade.durationMs);
    self._setTrackVolume(track, fade.startVol * (1 - self._easeInOut(t)));
    if (t < 1) {
      self._scheduleBgmFadeStep(step);
    } else {
      self._finishBgmFadeOut(scene);
    }
  }

  this._scheduleBgmFadeStep(step);
};

/**
 * Fade in one BGM loop while keeping other tracks silent (Phase II on bridge overlay).
 * @param {"fall"|"shake"} scene
 * @param {number} [durationMs]
 */
SoundManager.prototype.fadeInBgmTo = function (scene, durationMs) {
  if (this._isFadeInActive(scene)) return;
  if (this._bgmFade && this._bgmFade.mode === "in") {
    this._cancelBgmFade();
  }
  this._pendingBgmScene = scene || null;
  if (!this.enabled || !this.unlocked) return;
  if (!scene || !this.bgmTracks[scene]) {
    this.setBgmScene(scene);
    return;
  }

  var keys = Object.keys(this.bgmTracks);
  for (var i = 0; i < keys.length; i++) {
    if (keys[i] === scene || this._isFadeOutActive(keys[i])) continue;
    this.stopBgmTrack(keys[i]);
  }

  var track = this.bgmTracks[scene];
  var ms = Math.max(400, Math.floor(durationMs || 2800));
  var startAt = typeof performance !== "undefined" ? performance.now() : Date.now();
  var self = this;

  this._setTrackVolume(track, 0);
  if (!this.bgmPaused && !this.muted) this._startBgmPlayback(track);
  this.bgmScene = scene;
  this._pendingBgmScene = scene;

  this._bgmFade = { mode: "in", scene: scene, startAt: startAt, durationMs: ms };

  function step(now) {
    var fade = self._bgmFade;
    if (!fade || fade.mode !== "in" || fade.scene !== scene) return;
    var t = Math.min(1, (now - fade.startAt) / fade.durationMs);
    self._setTrackVolume(track, self.bgmVolume * self._easeInOut(t));
    if (t < 1) {
      self._scheduleBgmFadeStep(step);
    } else {
      self._finishBgmFadeIn(scene);
    }
  }

  this._scheduleBgmFadeStep(step);
};

/** @deprecated Use fadeInBgmTo after fall BGM has already faded out. */
SoundManager.prototype.crossfadeBgmTo = function (toScene, durationMs) {
  this.stopBgmTrack("fall");
  this.fadeInBgmTo(toScene, durationMs);
};

/**
 * @param {"fall"|"shake"|null} scene
 */
SoundManager.prototype.setBgmScene = function (scene) {
  var fadeInActive = this._isFadeInActive(scene);
  if (!fadeInActive) {
    this._cancelBgmFade();
  }
  this._pendingBgmScene = scene || null;
  if (!this.enabled || !this.unlocked) return;
  if (fadeInActive || this.bgmScene === scene) {
    if (scene) this.bgmScene = scene;
    if (scene && !this.bgmPaused && !fadeInActive) {
      var cur = this.bgmTracks[scene];
      if (cur) {
        this._setTrackVolume(cur, this.bgmVolume);
        if (cur.paused) this._startBgmPlayback(cur);
      }
    }
    return;
  }
  if (scene) {
    var alreadyPlaying = this.bgmTracks[scene];
    if (alreadyPlaying && !alreadyPlaying.paused) {
      this._stopAllBgmExcept(scene);
      this.bgmScene = scene;
      this._setTrackVolume(alreadyPlaying, this.bgmVolume);
      return;
    }
  }
  this._stopAllBgm();
  this.bgmScene = scene || null;
  if (!scene || this.bgmPaused) return;
  var next = this.bgmTracks[scene];
  if (next) {
    this._setTrackVolume(next, this.bgmVolume);
    this._startBgmPlayback(next);
  }
};

SoundManager.prototype.stopBgm = function () {
  this.setBgmScene(null);
};

SoundManager.prototype.setBgmPaused = function (paused) {
  this.bgmPaused = !!paused;
  var keys = Object.keys(this.bgmTracks);
  for (var i = 0; i < keys.length; i++) {
    var track = this.bgmTracks[keys[i]];
    if (!track) continue;
    try {
      if (this.bgmPaused) track.pause();
      else if (
        !this.muted &&
        this.enabled &&
        this.unlocked &&
        (this.bgmScene === keys[i] ||
          (this._bgmFade && this._bgmFade.scene === keys[i]))
      ) {
        this._startBgmPlayback(track);
      }
    } catch (e) { /* ignore */ }
  }
};

SoundManager.prototype.prepare = function () {
  this.prepareBgm();
  this.sfxEngine = this._createSfxEngine();
  this.sfxEngine.setMasterGain(this.sfxVolume);
  this.sfxEngine.setMuted(this.muted);
};

/**
 * Decode SFX into Web Audio buffers + warm BGM streams before gameplay.
 * @param {number} [timeoutMs]
 * @returns {Promise<void>}
 */
SoundManager.prototype.preloadAll = function (timeoutMs) {
  var self = this;
  if (typeof window === "undefined") return Promise.resolve();

  var tasks = [];
  if (this.sfxEngine && typeof this.sfxEngine.init === "function") {
    tasks.push(this.sfxEngine.init(timeoutMs));
  }

  if (typeof Audio !== "undefined") {
    var limit = timeoutMs != null ? timeoutMs : 10000;
    var seen = {};
    var bgmUrls = [];
    var bgmKeys = Object.keys(this.bgmManifest);
    for (var b = 0; b < bgmKeys.length; b++) {
      var bgmFile = this.bgmManifest[bgmKeys[b]];
      if (!bgmFile || seen[bgmFile]) continue;
      seen[bgmFile] = true;
      var bgmUrl = resolveSoundFileUrl(bgmFile);
      if (bgmUrl) bgmUrls.push(bgmUrl);
    }

    function waitForAudio(url) {
      return new Promise(function (resolve) {
        var audio = new Audio(url);
        audio.preload = "auto";
        var settled = false;
        function done() {
          if (settled) return;
          settled = true;
          clearTimeout(timer);
          audio.removeEventListener("canplaythrough", onReady);
          audio.removeEventListener("loadeddata", onReady);
          audio.removeEventListener("error", onReady);
          resolve();
        }
        function onReady() { done(); }
        var timer = setTimeout(done, limit);
        audio.addEventListener("canplaythrough", onReady, { once: true });
        audio.addEventListener("loadeddata", onReady, { once: true });
        audio.addEventListener("error", onReady, { once: true });
        try {
          audio.load();
        } catch (e) {
          done();
        }
      });
    }

    tasks.push(Promise.all(bgmUrls.map(waitForAudio)));
  }

  return Promise.all(tasks).then(function () {
    var trackKeys = Object.keys(self.bgmTracks);
    for (var t = 0; t < trackKeys.length; t++) {
      try {
        var tr = self.bgmTracks[trackKeys[t]];
        if (tr) tr.load();
      } catch (e3) {}
    }
  });
};

SoundManager.prototype._primeUnlockSfxAsync = function () {
  var self = this;
  if (!this._isSfxWanted()) {
    this.sfxAutoplayBlocked = false;
    return Promise.resolve(true);
  }
  if (!this.sfxEngine) {
    this.sfxAutoplayBlocked = false;
    return Promise.resolve(true);
  }
  return this.sfxEngine.tryUnlockAsync().then(function (ok) {
    self.sfxAutoplayBlocked = !ok;
    return ok;
  });
};

SoundManager.prototype._applyPendingBgmAfterUnlock = function () {
  if (!this._pendingBgmScene) return Promise.resolve(true);
  var scene = this._pendingBgmScene;
  var track = this.bgmTracks[scene];
  if (!track) return Promise.resolve(false);
  if (!this._isBgmWanted()) return Promise.resolve(true);
  var self = this;
  this._stopAllBgmExcept(scene);
  this.bgmScene = scene;
  if (this.bgmPaused) return Promise.resolve(true);
  return this._startBgmPlayback(track).then(function (ok) {
    if (!ok) self.bgmAutoplayBlocked = true;
    return ok;
  });
};

SoundManager.prototype._tryProgrammaticUnlock = function () {
  var self = this;
  if (this.unlocked) return Promise.resolve(true);
  return this._primeUnlockSfxAsync().then(function (sfxOk) {
    self.sfxAutoplayBlocked = !sfxOk;
    if (!self._isBgmWanted()) {
      // Web Audio SFX unlocks on the first user gesture; do not block boot.
      return true;
    }
    return self.ensureBgmPlayback().then(function (bgmOk) {
      if (!bgmOk) {
        self.bgmAutoplayBlocked = true;
        return false;
      }
      self.bgmAutoplayBlocked = false;
      if (sfxOk) {
        self.unlocked = true;
        self.sfxAutoplayBlocked = false;
        if (self.sfxEngine) self.sfxEngine.setUnlocked(true);
      }
      return true;
    });
  });
};

/**
 * @param {{ fromUserGesture?: boolean }} [opts]
 * @returns {Promise<boolean>}
 */
SoundManager.prototype.unlock = function (opts) {
  opts = opts || {};
  var fromGesture = !!opts.fromUserGesture;
  var self = this;
  if (fromGesture) {
    this.unlocked = true;
    this.bgmAutoplayBlocked = false;
    this.sfxAutoplayBlocked = false;
    if (this.sfxEngine) {
      this.sfxEngine.setUnlocked(true);
      return this.sfxEngine.unlock().then(function () {
        return self._applyPendingBgmAfterUnlock();
      });
    }
    return this._applyPendingBgmAfterUnlock();
  }
  if (this.unlocked) return Promise.resolve(true);
  return this._tryProgrammaticUnlock();
};

SoundManager.prototype.unlockFromUserGesture = function () {
  return this.unlock({ fromUserGesture: true });
};

SoundManager.prototype.playRandom = function (names) {
  var idx = Math.floor(Math.random() * names.length);
  this.play(names[idx]);
};

SoundManager.prototype.play = function (name) {
  if (!this.enabled || !this.unlocked || this.muted || this.focusPaused) return;
  if (!this.sfxEngine) return;
  var now = Date.now();
  var cooldown = this.cooldownsMs[name] || 0;
  var lastAt = this.lastPlayedAt[name] || 0;
  if (cooldown > 0 && now - lastAt < cooldown) return;
  this.lastPlayedAt[name] = now;
  this.sfxEngine.play(name, { volume: this.sfxVolume });
};

SoundManager.prototype.bindEvents = function () {
  var bus = EventBus;
  if (!bus) return;
  var dingNames = ["ding1", "ding2", "ding3", "ding4", "ding5"];
  var bounceCooldownMs = 40;
  bus.on("fall:gem", function () { this.play("gem"); }.bind(this));
  bus.on("fall:bonus", function () { this.play("shield"); }.bind(this));
  bus.on("fall:shield_block", function () { this.play("shield_boing"); }.bind(this));
  bus.on("fall:boulder", function (data) {
    if (data && data.shieldBlocked) return;
    this.play(data && data.isHpDepleted ? "flask_smash" : "flask_hit");
  }.bind(this));
  bus.on("fall:boulder_explode", function () {
    this.play("boulder_explode");
  }.bind(this));
  bus.on("fall:catch", function () { this.play("pickup"); }.bind(this));
  bus.on("fall:revive", function () { this.play("gold_orb"); }.bind(this));
  bus.on("merge:revive", function () { this.play("gold_orb"); }.bind(this));
  bus.on("fall:bounce", function () {
    var now = Date.now();
    if (now - this._lastBounceSfxAt < bounceCooldownMs) return;
    this._lastBounceSfxAt = now;
    this.playRandom(dingNames);
  }.bind(this));
  bus.on("flask:merge", function (data) {
    if (data && data.capped) return;
    this.play("merge");
  }.bind(this));
  bus.on("ui:button_click", function () { this.play("just_fall"); }.bind(this));
  bus.on("shake:life_gain", function () { this.play("life_up"); }.bind(this));
  bus.on("shake:drop", function () { this.play("shake_drop"); }.bind(this));
  bus.on("shake:shrink", function () { this.play("shrink"); }.bind(this));
};

SoundManager.prototype.attachUnlockHandlers = function () {
  var unlock = function () {
    this.unlock({ fromUserGesture: true });
  }.bind(this);
  window.addEventListener("pointerdown", unlock, { once: true });
  window.addEventListener("keydown", unlock, { once: true });
  window.addEventListener("touchstart", unlock, { once: true, passive: true });
};

SoundManager.prototype.init = function () {
  this.loadSettings();
  this.prepare();
  this._syncBgmTrackVolumes();
  this.bindEvents();
  this.attachUnlockHandlers();
};